/**
 * AgStack API Integration Utility
 *
 * This class provides methods to interact with the AgStack API for field boundary
 * registration and GeoJSON retrieval. It includes token caching, retry logic,
 * and timeout handling for improved reliability and performance.
 *
 * @class AgStackAPI
 */

const agStackCredentials = process.env.REACT_APP_AGSTACK;
const { geojsonToWKT } = require('@terraformer/wkt');

class AgStackAPI {
	constructor() {
		// API endpoints - can be overridden by environment variables
		this.assetUrl = process.env.REACT_APP_AGSTACK_ASSET_URL || 'https://api-ar.agstack.org';
		this.userUrl = process.env.REACT_APP_AGSTACK_USER_URL || 'https://user-registry.agstack.org';

		// Session management
		this.sessionCookie = null;

		// Token caching to avoid repeated authentication
		this.cachedToken = null;
		this.tokenExpiry = null;

		// Default timeout for fetch requests (10 seconds)
		this.defaultTimeout = 3000;
	}

	/**
	 * Fetch with retry logic and timeout handling
	 *
	 * This internal method wraps the native fetch API with:
	 * - Automatic retries with exponential backoff
	 * - Request timeout handling
	 * - Better error reporting
	 *
	 * @param {string} url - The URL to fetch
	 * @param {Object} options - Fetch options
	 * @param {number} retries - Number of retry attempts (default: 3)
	 * @param {number} timeout - Timeout in milliseconds (default: 10000)
	 * @returns {Promise<Response>} The fetch response
	 * @private
	 */
	async fetchWithRetry(url, options = {}, retries = 2, timeout = this.defaultTimeout) {
		for (let attempt = 1; attempt <= retries; attempt++) {
			try {
				// Create abort controller for timeout handling
				const controller = new AbortController();
				const timeoutId = setTimeout(() => controller.abort(), timeout);

				const response = await fetch(url, {
					...options,
					signal: controller.signal
				});

				clearTimeout(timeoutId);
				return response;

			} catch (error) {
				const isLastAttempt = attempt === retries;

				// Log the error
				console.warn(`[AgStackAPI] Attempt ${attempt}/${retries} failed for ${url}:`, error.message);

				if (isLastAttempt) {
					throw new Error(`Request failed after ${retries} attempts: ${error.message}`);
				}

				// Exponential backoff: wait 1s, 2s, 4s, etc.
				const backoffDelay = Math.pow(2, attempt) * 1000;
				console.log(`[AgStackAPI] Retrying in ${backoffDelay}ms...`);
				await new Promise(resolve => setTimeout(resolve, backoffDelay));
			}
		}
	}

	/**
	 * Get GeoJSON polygon from AgStack using a GeoID
	 *
	 * This method fetches the field boundary GeoJSON for a given GeoID.
	 * No authentication is required for this operation.
	 *
	 * @param {string} geoId - The AgStack GeoID of the field
	 * @returns {Promise<Object>} The GeoJSON object representing the field boundary
	 * @throws {Error} If the GeoJSON cannot be fetched or is not found
	 *
	 * @example
	 * const api = new AgStackAPI();
	 * const geoJson = await api.getGeoJSON('abc123...');
	 */
	async getGeoJSON(geoId) {
		try {
			const response = await this.fetchWithRetry(`${this.assetUrl}/fetch-field/${geoId}`, {
				method: 'GET',
			});

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const json = await response.json();

			if (json && json['Geo JSON'] !== undefined) {
				console.log(`[AgStackAPI] Successfully fetched GeoJSON for ${geoId}`);
				return json['Geo JSON'];
			} else {
				throw new Error(`GeoJSON not found in response for GeoID: ${geoId}`);
			}
		} catch (error) {
			console.error(`[AgStackAPI] Failed to fetch GeoJSON for ${geoId}:`, error);
			throw error;
		}
	}


	/**
	 * Get AgStack authentication token with caching
	 *
	 * This method authenticates with the AgStack API and caches the token
	 * to avoid repeated authentication requests. Tokens are cached for 55 minutes
	 * (with a 5-minute safety margin from the standard 1-hour expiry).
	 *
	 * The method will:
	 * 1. Return cached token if still valid
	 * 2. Otherwise, authenticate and cache the new token
	 * 3. Return null if credentials are not configured or authentication fails
	 *
	 * @returns {Promise<string|null>} The access token, or null if authentication fails
	 *
	 * @example
	 * const api = new AgStackAPI();
	 * const token = await api.getAgstackToken();
	 * if (token) {
	 *   // Use token for authenticated requests
	 * }
	 */
	async getAgstackToken() {
		// Check if credentials are configured
		if (agStackCredentials === undefined) {
			console.warn("[AgStackAPI] Credentials not found. Please set REACT_APP_AGSTACK environment variable.");
			return null;
		}

		// Return cached token if still valid
		if (this.cachedToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
			console.log('[AgStackAPI] Using cached authentication token');
			return this.cachedToken;
		}

		console.log('[AgStackAPI] Authenticating with AgStack...');

		// Define the authentication endpoint
		const authUrl = `${this.assetUrl}/login`;

		try {
			// Make a POST request to authenticate and obtain the token
			const response = await this.fetchWithRetry(authUrl, {
				method: 'POST',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				},
				body: agStackCredentials
			});

			// Check if the authentication was successful
			if (response.ok) {
				// Extract the token from the response
				const data = await response.json();
				const token = data['access_token'];

				if (token) {
					// Cache the token with 55-minute expiry (5-minute safety margin)
					this.cachedToken = token;
					this.tokenExpiry = Date.now() + (55 * 60 * 1000);
					console.log('[AgStackAPI] Authentication successful, token cached');
					return token;
				} else {
					console.error('[AgStackAPI] No access_token in response');
					return null;
				}
			} else {
				// Log the error if authentication failed
				const errorText = await response.text();
				console.error(`[AgStackAPI] Authentication failed with status ${response.status}:`, errorText);
				return null;
			}
		} catch (error) {
			// Log any network error
			console.error('[AgStackAPI] Authentication error:', error.message);
			return null;
		}
	}


	/**
	 * Register a field boundary with AgStack
	 *
	 * This method registers a GeoJSON field boundary with the AgStack API.
	 * It converts the GeoJSON to WKT format and sends it to AgStack.
	 *
	 * Features:
	 * - Validates GeoJSON input
	 * - Handles FeatureCollections by extracting the first feature
	 * - Uses cached authentication token
	 * - Returns existing GeoID if field already registered
	 * - Includes retry logic via fetchWithRetry
	 *
	 * @param {Object} geoJson - The GeoJSON object to register
	 * @param {boolean} debug - Enable debug logging (default: false)
	 * @returns {Promise<string|Object>} The GeoID string if successful, or an object with errorMessage
	 *
	 * @example
	 * const api = new AgStackAPI();
	 * const geoId = await api.registerGeoJson(myGeoJson);
	 * if (typeof geoId === 'string') {
	 *   console.log('Registered with GeoID:', geoId);
	 * } else {
	 *   console.error('Registration failed:', geoId.errorMessage);
	 * }
	 */
	async registerGeoJson(geoJson, debug = false) {
		// Check if credentials are configured
		if (agStackCredentials === undefined) {
			console.warn("[AgStackAPI] Credentials not found, skipping registration. Please set REACT_APP_AGSTACK.");
			return null;
		}

		// Validate GeoJSON structure
		if (geoJson.type === undefined && geoJson.geometry === undefined) {
			throw new Error('[AgStackAPI] Invalid GeoJSON: missing type and geometry properties');
		}

		// Handle FeatureCollections
		if (geoJson.type === "FeatureCollection") {
			if (!geoJson.features || geoJson.features.length === 0) {
				throw new Error('[AgStackAPI] FeatureCollection is empty');
			}
			console.warn("[AgStackAPI] GeoJSON is a FeatureCollection. Using first feature (AgStack accepts single WKT).");
			geoJson = geoJson.features[0];
		}

		try {
			// Convert GeoJSON geometry to WKT format
			const wkt = geojsonToWKT(geoJson.geometry);

			// Define the request payload
			const payload = { wkt };

			// Make the POST request
			const url = `${this.assetUrl}/register-field-boundary`;

			// Get the authentication token (uses cache if available)
			const token = await this.getAgstackToken();
			const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

			if (debug) console.log("[AgStackAPI] Registering field boundary...");

			const response = await this.fetchWithRetry(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...headers
				},
				body: JSON.stringify(payload)
			});

			// Process the response
			const jsonResponse = await response.json();

			if (response.ok) {
				// Successful registration
				const geoId = jsonResponse['Geo Id'];

				if (geoId) {
					console.log(`[AgStackAPI] Field boundary registered successfully! GeoID: ${geoId}`);
					return geoId;
				} else {
					console.warn("[AgStackAPI] Registration response missing GeoID:", jsonResponse);
					return {
						errorMessage: jsonResponse.message || 'Unknown error: no GeoID in response'
					};
				}
			} else {
				// Registration failed - check if field already exists
				const matchedGeoIds = jsonResponse["matched geo ids"];

				if (matchedGeoIds && matchedGeoIds.length > 0) {
					const existingGeoId = matchedGeoIds[0];
					if (debug) {
						console.log(`[AgStackAPI] Field already registered (HTTP ${response.status}). Using existing GeoID: ${existingGeoId}`);
					}
					return existingGeoId;
				} else {
					console.error(`[AgStackAPI] Registration failed (HTTP ${response.status}):`, jsonResponse);
					return {
						errorMessage: jsonResponse.message || `Registration failed with status ${response.status}`
					};
				}
			}
		} catch (error) {
			console.error('[AgStackAPI] Error registering field boundary:', error);
			return {
				errorMessage: error.message || 'Unknown error during registration'
			};
		}
	}

}

export default AgStackAPI;

/*
// test the class above
const api = new AgStackAPI();
console.log("Test the class above");

	api.getGeoJSON('88bec54ad04804f5b1fafbc131266640a129be2840fa6797cda358d7e831b907').then( geoJson => {
		console.log("Polygon fetched succesfully", geoJson );
	}).catch(err => {
		console.log("Error Getting Polygon", err);
	})
*/